import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.tsx'
import useCollectionDetailsModalStore, { OnSuccessfulFinalActionMode } from '@/useCollectionDetailsModalStore.ts'
import { BookmarkCheck, FilePlus2, Info, Loader2, Pencil, Trash, Undo2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button.tsx'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils.ts'
import { toast } from 'sonner'
import useGlobalStore from '@/useGlobalStore.ts'
import dayjs from 'dayjs'
import api from '@/api.ts'
import { SheetDescription } from '@/components/ui/sheet.tsx'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { FloatingInput, FloatingLabel } from '@/components/ui/floating-input.tsx'
import { LibraryCollection } from '@/models.ts'
import { dispatch, useBus } from '@/useBus.ts'

const DEFAULT_COLOR = '#FF6900'

const CollectionDetailsDialog = () => {
	const { t } = useTranslation()
	const { appLanguageCode, isTrialExpired, availableAppLanguages } = useGlobalStore()
	const { mode, setMode, isOpen, data, setData, hideModal, showModal, onSuccessfulFinalAction } =
		useCollectionDetailsModalStore()
	const [isDeleting, setIsDeleting] = useState(false)
	const [isSubmittingNewWord, setIsSubmittingNewWord] = useState(false)
	const [wordTappedForDeletion, setWordTappedForDeletion] = useState(false)
	const [createdAt, setCreatedAt] = useState('')
	const [name, setName] = useState('')
	const [color, setColor] = useState(DEFAULT_COLOR)
	const availableColors = ['#FF6900', '#FCB900', '#00796b', '#00D084', '#8f2d56', '#0693E3', '#EB144C', '#9900EF']

	useEffect(() => {
		if (mode === 'saved' && data !== null && data.name !== '' && data.color !== '') {
			setName(data.name)
			setColor(data.color)
		}
	}, [data, mode])

	useEffect(() => {
		const targetDate = dayjs(data?.created_at)
		if (dayjs().diff(targetDate, 'day') < 7) {
			setCreatedAt(targetDate.fromNow())
		} else {
			setCreatedAt(targetDate.format('YYYY-MM-DD'))
		}
		setWordTappedForDeletion(false)
	}, [data?.created_at])

	useEffect(() => {
		setWordTappedForDeletion(false)
	}, [isOpen])

	useEffect(() => {
		if (mode === 'add') {
			setName('')
			setColor(DEFAULT_COLOR)
		}
	}, [mode])

	useBus('close-modal', () => {
		hideModal()
	})

	const deleteWord = async () => {
		setIsDeleting(true)
		await api(`/v2/library/collections/${data?.id}`, {
			method: 'DELETE',
		})
			.then(() => hideModal())
			.catch(() => toast(t('Unexpected error')))
			.finally(() => setIsDeleting(false))
		if (onSuccessfulFinalAction !== null) {
			onSuccessfulFinalAction(OnSuccessfulFinalActionMode.DELETE)
		}
	}

	const submitWord = async () => {
		if (mode.includes('add')) {
			await submitNewWord()
		} else if (mode.includes('edit')) {
			await submitEditWord()
		}
	}

	const submitNewWord = async () => {
		if (isTrialExpired) {
			dispatch('show-auth-dialog', {
				trialExpired: true,
			})
			return
		}
		setIsSubmittingNewWord(true)
		await api(`/v2/library/collections/`, {
			method: 'POST',
			body: {
				name: name,
				color: color,
			},
		})
			.then((libCol: LibraryCollection) => {
				setName('')
				if (onSuccessfulFinalAction !== null) {
					onSuccessfulFinalAction(OnSuccessfulFinalActionMode.ADD, libCol)
				}
				hideModal()
			})
			.catch((x) => {
				// todo: show upgrade page
				toast(x.data?.message ?? t('Something went wrong'))
			})
			.finally(() => setIsSubmittingNewWord(false))
	}

	const submitEditWord = async () => {
		setIsSubmittingNewWord(true)
		await api(`/v2/library/collections/${data?.id}`, {
			method: 'PUT',
			body: {
				name: name,
				color: color,
			},
		})
			.then((libCol: LibraryCollection) => {
				setName('')
				if (onSuccessfulFinalAction !== null) {
					onSuccessfulFinalAction(OnSuccessfulFinalActionMode.EDIT, libCol)
				}
				setData(libCol)
				setMode('saved')
			})
			.catch((x) => {
				toast(x.data?.message ?? t('Something went wrong'))
			})
			.finally(() => setIsSubmittingNewWord(false))
	}

	const tappedForEdit = () => {
		setMode('edit')
		if (data !== null) {
			setName(data.name)
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={isOpen ? hideModal : showModal}>
			<DialogContent
				dir={availableAppLanguages[appLanguageCode]?.dir}
				hideClose
				className="p-3 w-[95%] rounded-lg">
				<VisuallyHidden.Root>
					<DialogTitle>Collection details dialog</DialogTitle>
					<SheetDescription>Collection details dialog</SheetDescription>
				</VisuallyHidden.Root>
				<DialogHeader>
					<div className="text-sm text-left flex items-center justify-between mb-1">
						<span className="flex items-center">
							<BookmarkCheck className="inline-flex w-4 me-1" />
							{mode.includes('saved') ? t('Saved collection') : t('Save collection')}
						</span>
						<Button onClick={hideModal} variant="ghost" size="icon" className="w-7 h-7">
							<X className="w-4" />
						</Button>
					</div>
					{mode.includes('saved') && (
						<div className="flex !m-0">
							<small>
								{t('Saved on')}:<span className="ms-1">{createdAt}</span>
							</small>
						</div>
					)}
					<div className="!m-0">
						<div className={`flex justify-between ${mode === 'saved' ? 'my-4' : ''}`}>
							<div className="my-2 flex-grow">
								<div className="flex justify-between">
									{mode.includes('saved') ? (
										<h4 className="flex text-xl font-normal items-center justify-start">
											{data?.name}
										</h4>
									) : (
										<div className="relative w-full me-1">
											<FloatingInput
												tabIndex={1}
												// autoCapitalize="none"
												autoFocus={name?.length === 0}
												autoComplete="off"
												className="!outline-none !ring-0 text-base"
												id="name"
												value={name}
												onChange={(val) => setName(val.target.value)}
											/>
											<FloatingLabel htmlFor="name" className="text-muted-foreground">
												{t('Name')}
											</FloatingLabel>
										</div>
									)}
								</div>
								<div className="flex justify-between mt-3">
									{mode.includes('saved') ? (
										<div className="w-7 h-7 rounded-md" style={{ background: color }}></div>
									) : (
										<div className="flex justify-between border border-input rounded-md h-10 w-full me-1">
											<div className="text-muted-foreground flex justify-center items-center text-sm ms-4 text-start">
												{t('Color')}
											</div>
											<div className="flex justify-center">
												{availableColors.map((clr, idx) => (
													<div
														key={idx}
														onClick={() => setColor(clr)}
														className={`cursor-pointer w-7 h-auto ${idx === availableColors.length - 1 ? 'rounded-e-md' : ''} ${clr === color ? 'ring-4 ring-white ring-inset' : ''}`}
														style={{ background: clr }}></div>
												))}
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="mt-3 flex justify-between">
							{mode === 'saved/no-edit' ? (
								<small className="text-left flex items-center text-gray-500">
									<Info className="w-4 h-4 me-2" />
									{t('Edit or delete from your library')}
								</small>
							) : (
								<>
									{!wordTappedForDeletion && <div></div>}
									{mode.includes('saved') ? (
										<div>
											<Button
												className="me-1"
												disabled={isDeleting}
												variant={wordTappedForDeletion ? 'destructive' : 'ghost'}
												onClick={() => setWordTappedForDeletion(true)}>
												{!isDeleting && (
													<Trash className={cn('w-4', wordTappedForDeletion ? 'me-2' : '')} />
												)}
												{wordTappedForDeletion &&
													(!isDeleting ? (
														<span onClick={deleteWord}>{t('Tap to remove')}</span>
													) : (
														<Loader2 className="h-4 w-4 animate-spin" />
													))}
											</Button>
											{!wordTappedForDeletion && (
												<Button onClick={() => tappedForEdit()}>
													<Pencil className="w-4 me-2" />
													<span>{t('Edit')}</span>
												</Button>
											)}
										</div>
									) : (
										<Button
											onClick={submitWord}
											tabIndex={4}
											disabled={isSubmittingNewWord || name?.length <= 0}
											variant="outline">
											{isSubmittingNewWord ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<FilePlus2 className="h-4 w-4" />
											)}
											<span className="ms-2">{t('Save')}</span>
										</Button>
									)}
									{wordTappedForDeletion && (
										<Button variant="outline" onClick={() => setWordTappedForDeletion(false)}>
											<Undo2 className="me-2 w-5" />
											{t('Cancel')}
										</Button>
									)}
								</>
							)}
						</div>
					</div>
				</DialogHeader>
			</DialogContent>
		</Dialog>
	)
}

export default CollectionDetailsDialog
